import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { agentTickets } from '../../../services/api';
import {
  ArrowLeft, Clock, UserCheck, AlertTriangle, Send,
  FileText, ChevronDown, ChevronUp, RefreshCw,
  Archive, BellOff, RotateCcw, MessageSquare, Trash2,
  Lock, ShieldAlert,
} from 'lucide-react';

// Rejection category options — matches DB ENUM
const REJECTION_CATEGORIES = [
  { value: '',                label: 'Select a reason (optional)' },
  { value: 'spam',            label: 'Spam / unsolicited' },
  { value: 'off_topic',       label: 'Off topic / wrong inbox' },
  { value: 'already_handled', label: 'Already handled' },
  { value: 'wrong_draft',     label: 'Wrong draft — needs manual reply' },
  { value: 'other',           label: 'Other' },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function parseC7(raw) {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
}

function parseToneFlags(raw) {
  if (!raw) return [];
  try {
    const val = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(val) ? val : [];
  } catch { return []; }
}

const INTENT_LABELS = {
  order_request:     'Order Request',
  tasting_booking:   'Tasting Booking',
  club_change:       'Club Change',
  group_reservation: 'Group Reservation',
  unknown:           'Unknown',
};

const STATUS_COLORS = {
  pending:   'warning',
  reviewing: 'info',
  approved:  'success',
  sent:      'dark',
  rejected:  'danger',
};

const TONE_COLORS = {
  frustrated: 'danger',
  urgent:     'warning',
  confused:   'info',
  positive:   'success',
};

function ConfidenceBar({ score }) {
  if (score == null) return null;
  const pct   = Math.round(score * 100);
  const color = score >= 0.7 ? 'success' : score >= 0.4 ? 'warning' : 'danger';
  return (
    <div className="d-flex align-items-center gap-2">
      <div className="progress flex-grow-1" style={{ height: 6 }}>
        <div className={`progress-bar bg-${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-${color} fw-semibold`} style={{ fontSize: 13, minWidth: 34 }}>{pct}%</span>
    </div>
  );
}

// Send-delay countdown (seconds remaining until approved reply can be sent)
const SEND_DELAY_SECONDS = 300; // mirrors AGENT_SEND_DELAY_SECONDS default

function computeSecondsRemaining(actions) {
  if (!actions?.length) return 0;
  const approveAction = [...actions]
    .filter((a) => a.actionType === 'approved' || a.actionType === 'edited_and_approved')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  if (!approveAction) return 0;
  const elapsed = (Date.now() - new Date(approveAction.createdAt).getTime()) / 1000;
  return Math.max(0, Math.ceil(SEND_DELAY_SECONDS - elapsed));
}

// ── AgentTicketDetail ────────────────────────────────────────────────────────

export default function AgentTicketDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const ticketID  = parseInt(id, 10);

  // ── Core state ─────────────────────────────────────────────────────────────
  const [ticket,      setTicket]      = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionMsg,   setActionMsg]   = useState('');

  // Editable reply body — pre-populated from latest draft
  const [editedBody, setEditedBody] = useState('');

  // Email history accordion
  const [historyOpen, setHistoryOpen] = useState(false);

  // Close/reopen + snooze
  const [closingTicket,  setClosingTicket]  = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [snoozeOpen,     setSnoozeOpen]     = useState(false);
  const [snoozing,       setSnoozing]       = useState(false);

  // Internal notes
  const [notes,          setNotes]          = useState([]);
  const [noteText,       setNoteText]       = useState('');
  const [savingNote,     setSavingNote]     = useState(false);
  const [deletingNote,   setDeletingNote]   = useState({});

  // Related (prior) tickets from same customer
  const [relatedTickets, setRelatedTickets] = useState([]);

  // Gmail thread messages — lazy-fetched when accordion opens
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading,  setThreadLoading]  = useState(false);
  const [threadFetched,  setThreadFetched]  = useState(false);
  const [threadError,    setThreadError]    = useState('');

  // Reply templates
  const [templates,      setTemplates]      = useState([]);

  // Manual compose mode — bypasses AI draft, clears textarea
  const [manualMode,     setManualMode]     = useState(false);

  // Draft regeneration + guidance hint
  const [regenerating,   setRegenerating]   = useState(false);
  const [hintOpen,       setHintOpen]       = useState(false);
  const [regenerateHint, setRegenerateHint] = useState('');

  // Cancel pending send
  const [cancellingSend, setCancellingSend] = useState(false);

  // Claim / soft lock
  const [lockedByOther, setLockedByOther] = useState(null); // { name, lockedAt }

  // ── Modal state ────────────────────────────────────────────────────────────
  const [showConfirm,       setShowConfirm]       = useState(false);
  const [pendingAction,     setPendingAction]     = useState(''); // 'approved' | 'edited_and_approved'
  const [showReject,        setShowReject]        = useState(false);
  const [rejectReason,      setRejectReason]      = useState('');
  const [rejectionCategory, setRejectionCategory] = useState('');
  const [showEscalate,      setShowEscalate]      = useState(false);
  const [escalateReason,    setEscalateReason]    = useState('');

  // Send-delay countdown (ticks while confirm modal is open)
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchTicket = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await agentTickets.get(ticketID);
      const data = res.data.data;
      setTicket(data);
      setNotes(data.notes || []);
      setRelatedTickets(data.relatedTickets || []);
      // Pre-populate textarea with the most recent draft body (only on first load)
      setEditedBody((prev) => prev || data.draft?.draftBody || '');
    } catch {
      setError('Failed to load ticket.');
    } finally {
      setLoading(false);
    }
  }, [ticketID]);

  // ── Snooze helpers ─────────────────────────────────────────────────────────
  const SNOOZE_OPTIONS = [
    { value: '1h',       label: '1 hour' },
    { value: '4h',       label: '4 hours' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: '3d',       label: '3 days' },
    { value: '1w',       label: '1 week' },
  ];

  function computeSnoozeUntil(duration) {
    const now = Date.now();
    switch (duration) {
      case '1h':       return new Date(now + 1  * 60 * 60 * 1000).toISOString();
      case '4h':       return new Date(now + 4  * 60 * 60 * 1000).toISOString();
      case 'tomorrow': return new Date(now + 24 * 60 * 60 * 1000).toISOString();
      case '3d':       return new Date(now + 3  * 24 * 60 * 60 * 1000).toISOString();
      case '1w':       return new Date(now + 7  * 24 * 60 * 60 * 1000).toISOString();
      default:         return null;
    }
  }

  // Close snooze dropdown on outside click
  useEffect(() => {
    if (!snoozeOpen) return;
    const close = () => setSnoozeOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [snoozeOpen]);

  // Load reply templates once on mount — non-fatal if unavailable
  useEffect(() => {
    agentTickets.getTemplates()
      .then((res) => setTemplates(res.data.data.templates || []))
      .catch(() => {});
  }, []);

  // Claim / soft lock — acquire on mount, release on unmount
  // 409 means locked by another rep — show informational banner (non-blocking)
  useEffect(() => {
    agentTickets.lock(ticketID)
      .catch((err) => {
        if (err.response?.status === 409 && err.response?.data?.locked) {
          setLockedByOther({
            name:     err.response.data.lockedByName || 'Another rep',
            lockedAt: err.response.data.lockedAt,
          });
        }
        // Other errors (e.g. v11 not migrated yet) are silently ignored
      });
    return () => {
      agentTickets.unlock(ticketID).catch(() => {});
    };
  }, [ticketID]);

  // ── Thread fetch ───────────────────────────────────────────────────────────
  const handleThreadOpen = async () => {
    const opening = !historyOpen;
    setHistoryOpen(opening);
    if (opening && !threadFetched) {
      setThreadLoading(true);
      setThreadError('');
      try {
        const res = await agentTickets.getThread(ticketID);
        setThreadMessages(res.data.data?.messages || []);
        setThreadFetched(true);
      } catch {
        setThreadError('Could not load thread from Gmail.');
      } finally {
        setThreadLoading(false);
      }
    }
  };

  // ── Regenerate draft ───────────────────────────────────────────────────────
  const handleRegenerate = async () => {
    setRegenerating(true);
    setActionError('');
    try {
      const res = await agentTickets.regenerateDraft(ticketID, regenerateHint.trim() || undefined);
      setEditedBody(res.data.data.draftBody);
      setManualMode(false);
      setHintOpen(false);
      setRegenerateHint('');
      setActionMsg('New draft generated.');
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to regenerate draft.');
    } finally {
      setRegenerating(false);
    }
  };

  // ── Cancel pending send ────────────────────────────────────────────────────
  const handleCancelSend = async () => {
    setCancellingSend(true);
    setActionError('');
    try {
      await agentTickets.cancelSend(ticketID);
      setActionMsg('Send cancelled — ticket is back in review. Make your changes and re-approve.');
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to cancel send. The email may have already been sent.');
    } finally {
      setCancellingSend(false);
    }
  };

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  // ── Countdown while confirm modal is open ─────────────────────────────────
  useEffect(() => {
    if (showConfirm) {
      setCountdown(computeSecondsRemaining(ticket?.actions));
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    } else {
      clearInterval(countdownRef.current);
    }
    return () => clearInterval(countdownRef.current);
  }, [showConfirm, ticket?.actions]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const openConfirm = (actionType) => {
    setActionError('');
    setPendingAction(actionType);
    setShowConfirm(true);
  };

  const handleApprove = async () => {
    setSubmitting(true);
    setActionError('');
    try {
      await agentTickets.approve(ticketID, editedBody, pendingAction || 'approved');
      setShowConfirm(false);
      setActionMsg('Reply approved and queued for sending.');
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to approve. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    const noteRequired = !rejectionCategory || rejectionCategory === 'other';
    if (noteRequired && !rejectReason.trim()) return;
    setSubmitting(true);
    setActionError('');
    try {
      await agentTickets.reject(ticketID, rejectReason.trim(), rejectionCategory || undefined);
      setShowReject(false);
      setRejectReason('');
      setRejectionCategory('');
      setActionMsg('Ticket rejected.');
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to reject. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEscalate = async () => {
    if (!escalateReason.trim()) return;
    setSubmitting(true);
    setActionError('');
    try {
      await agentTickets.escalate(ticketID, escalateReason.trim());
      setShowEscalate(false);
      setEscalateReason('');
      setActionMsg('Ticket escalated to human review.');
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to escalate. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePushToDraft = async () => {
    setSubmitting(true);
    setActionError('');
    try {
      const res = await agentTickets.pushToGmailDraft(ticketID, editedBody);
      const { draftUrl } = res.data.data || {};
      setActionMsg(
        draftUrl
          ? `Gmail draft created. `
          : 'Gmail draft created.'
      );
      if (draftUrl) {
        // Open Gmail draft in new tab
        window.open(draftUrl, '_blank', 'noopener,noreferrer');
      }
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to push to Gmail draft.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Close / Reopen ─────────────────────────────────────────────────────────
  const handleClose = async () => {
    setShowCloseModal(false);
    setClosingTicket(true);
    try {
      await agentTickets.close(ticketID);
      setActionMsg('Ticket closed and archived.');
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to close ticket.');
    } finally {
      setClosingTicket(false);
    }
  };

  const handleReopen = async () => {
    setClosingTicket(true);
    try {
      await agentTickets.reopen(ticketID);
      setActionMsg('Ticket reopened.');
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to reopen ticket.');
    } finally {
      setClosingTicket(false);
    }
  };

  // ── Snooze ─────────────────────────────────────────────────────────────────
  const handleSnooze = async (duration) => {
    setSnoozeOpen(false);
    const until = computeSnoozeUntil(duration);
    if (!until) return;
    setSnoozing(true);
    try {
      await agentTickets.snooze(ticketID, until);
      setActionMsg('Ticket snoozed — it will reappear in the queue when the time is up.');
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to snooze ticket.');
    } finally {
      setSnoozing(false);
    }
  };

  const handleUnsnooze = async () => {
    setSnoozing(true);
    try {
      await agentTickets.unsnooze(ticketID);
      setActionMsg('Snooze cleared — ticket is back in the active queue.');
      fetchTicket();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to clear snooze.');
    } finally {
      setSnoozing(false);
    }
  };

  // ── Notes ──────────────────────────────────────────────────────────────────
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await agentTickets.createNote(ticketID, noteText.trim());
      setNoteText('');
      // Refetch notes via ticket detail endpoint
      const res = await agentTickets.get(ticketID);
      setNotes(res.data.data.notes || []);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to add note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteID) => {
    setDeletingNote((prev) => ({ ...prev, [noteID]: true }));
    try {
      await agentTickets.deleteNote(ticketID, noteID);
      setNotes((prev) => prev.filter((n) => n.noteID !== noteID));
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to delete note.');
    } finally {
      setDeletingNote((prev) => ({ ...prev, [noteID]: false }));
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="alert alert-danger">
        {error || 'Ticket not found.'}
        <button className="btn btn-sm btn-link" onClick={() => navigate('/admin/agent/queue')}>
          Back to queue
        </button>
      </div>
    );
  }

  const c7          = parseC7(ticket.draft?.c7ContextSnapshot);
  const toneFlags   = parseToneFlags(ticket.toneFlags);
  const isDone      = ['sent', 'rejected'].includes(ticket.status);
  const isReadOnly  = isDone;
  const charCount   = editedBody.length;

  // Prior email history (all except the current message)
  return (
    <div>
      {/* Page header */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
          onClick={() => navigate('/admin/agent/queue')}
        >
          <ArrowLeft size={14} /> Queue
        </button>
        <div className="flex-grow-1">
          <h5 className="mb-0 text-truncate">{ticket.subject || '(No subject)'}</h5>
          <div className="d-flex align-items-center gap-2 mt-1">
            <span className={`badge bg-${STATUS_COLORS[ticket.status] || 'secondary'}`} style={{ fontSize: 11 }}>
              {ticket.status}
            </span>
            <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
              {INTENT_LABELS[ticket.intentType] || ticket.intentType || 'Unknown'}
            </span>
            {ticket.escalationLevel === 'human_only' && (
              <span className="badge bg-danger" style={{ fontSize: 11 }}>HUMAN ONLY</span>
            )}
            {ticket.escalationLevel === 'priority' && (
              <span className="badge bg-warning text-dark" style={{ fontSize: 11 }}>PRIORITY</span>
            )}
          </div>
        </div>
        {/* Snooze dropdown */}
        {!ticket.isClosed && (
          <div className="dropdown" style={{ position: 'relative' }}>
            <button
              className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              onClick={(e) => { e.stopPropagation(); setSnoozeOpen((o) => !o); }}
              disabled={snoozing}
              title="Snooze this ticket"
            >
              {snoozing
                ? <span className="spinner-border spinner-border-sm" />
                : <><BellOff size={13} /> Snooze</>}
            </button>
            {snoozeOpen && (
              <ul
                className="dropdown-menu show"
                style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1050, minWidth: 130 }}
                onClick={(e) => e.stopPropagation()}
              >
                {SNOOZE_OPTIONS.map((opt) => (
                  <li key={opt.value}>
                    <button
                      className="dropdown-item"
                      style={{ fontSize: 13 }}
                      onClick={() => handleSnooze(opt.value)}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Snoozed wake-up button */}
        {ticket.snoozedUntil && !ticket.isClosed && (
          <button
            className="btn btn-sm btn-outline-warning d-flex align-items-center gap-1"
            onClick={handleUnsnooze}
            disabled={snoozing}
            title="Clear snooze and return to queue"
          >
            <RotateCcw size={13} /> Wake Up
          </button>
        )}

        {/* Close / Reopen */}
        {ticket.isClosed ? (
          <button
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={handleReopen}
            disabled={closingTicket}
          >
            {closingTicket
              ? <span className="spinner-border spinner-border-sm" />
              : <><RotateCcw size={13} /> Reopen</>}
          </button>
        ) : (
          <button
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={() => setShowCloseModal(true)}
            disabled={closingTicket}
            title="Archive this ticket"
          >
            <Archive size={13} /> Close
          </button>
        )}

        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
          onClick={fetchTicket}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Action messages */}
      {actionMsg && (
        <div className="alert alert-success py-2 d-flex justify-content-between align-items-center">
          {actionMsg}
          <button type="button" className="btn-close" onClick={() => setActionMsg('')} />
        </div>
      )}
      {actionError && (
        <div className="alert alert-danger py-2 d-flex justify-content-between align-items-center">
          {actionError}
          <button type="button" className="btn-close" onClick={() => setActionError('')} />
        </div>
      )}

      {/* Archived notice */}
      {ticket.isClosed && (
        <div className="alert alert-secondary py-2 d-flex align-items-center gap-2">
          <Archive size={15} className="flex-shrink-0" />
          <span className="small flex-grow-1">
            This ticket is <strong>archived</strong> and hidden from the queue.
          </span>
          <button
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={handleReopen}
            disabled={closingTicket}
          >
            {closingTicket
              ? <span className="spinner-border spinner-border-sm" />
              : <><RotateCcw size={13} /> Reopen</>}
          </button>
        </div>
      )}

      {/* Snoozed notice */}
      {ticket.snoozedUntil && !ticket.isClosed && (
        <div className="alert alert-warning py-2 d-flex align-items-center gap-2">
          <BellOff size={15} className="flex-shrink-0" />
          <span className="small flex-grow-1">
            Snoozed until <strong>{fmt(ticket.snoozedUntil)}</strong> — hidden from the queue until then.
          </span>
          <button
            className="btn btn-sm btn-outline-warning d-flex align-items-center gap-1"
            onClick={handleUnsnooze}
            disabled={snoozing}
          >
            {snoozing
              ? <span className="spinner-border spinner-border-sm" />
              : <><RotateCcw size={13} /> Wake Up</>}
          </button>
        </div>
      )}

      {/* Lock banner — informational, shown when another rep has this open */}
      {lockedByOther && (
        <div className="alert alert-warning py-2 d-flex align-items-center gap-2">
          <Lock size={15} className="flex-shrink-0" />
          <span className="small flex-grow-1">
            <strong>{lockedByOther.name}</strong> is currently reviewing this ticket.
            You can still view and edit — your changes won't be lost.
          </span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setLockedByOther(null)}
            title="Dismiss"
          />
        </div>
      )}

      {/* Two-column layout */}
      <div className="row g-4">

        {/* ── Left column (7/12): email + draft editor ── */}
        <div className="col-12 col-lg-7">

          {/* Original email */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-light py-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-semibold small">Original Email</span>
                <span className="text-muted small d-flex align-items-center gap-1">
                  <Clock size={12} /> {fmt(ticket.createdAt)}
                </span>
              </div>
              <div className="text-muted small mt-1">
                <strong>From:</strong> {ticket.customerName
                  ? `${ticket.customerName} <${ticket.customerEmail}>`
                  : ticket.customerEmail}
              </div>
            </div>
            <div className="card-body">
              <pre
                className="mb-0 text-dark"
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  lineHeight: 1.6,
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                {ticket.rawEmail?.body || ticket.bodyPreview || '(No email body available)'}
              </pre>
            </div>
          </div>

          {/* Draft editor */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
              <span className="fw-semibold small">
                {manualMode ? 'Manual Reply' : 'Agent Draft — Edit Before Sending'}
              </span>
              <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
                {!isReadOnly && !regenerating && (
                  <button
                    className="btn btn-link btn-sm p-0 text-muted d-flex align-items-center gap-1"
                    style={{ fontSize: 12, textDecoration: 'none' }}
                    onClick={() => setHintOpen((o) => !o)}
                    disabled={submitting || regenerating}
                    title="Re-run AI draft (optionally with guidance)"
                  >
                    <RefreshCw size={12} />
                    Regenerate
                  </button>
                )}
                {regenerating && (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10 }} />
                    Generating…
                  </span>
                )}
                <div className="vr mx-1" style={{ height: 14 }} />
                <input
                  className="form-check-input mt-0"
                  type="checkbox"
                  id="manualModeToggle"
                  checked={manualMode}
                  onChange={(e) => {
                    setManualMode(e.target.checked);
                    if (e.target.checked) setEditedBody('');
                  }}
                  disabled={isReadOnly}
                />
                <label className="form-check-label small text-muted" htmlFor="manualModeToggle">
                  Manual
                </label>
              </div>
            </div>
            <div className="card-body">
              {/* Template loader — hidden in manual mode (you'd use template OR write from scratch) */}
              {!isReadOnly && !manualMode && templates.length > 0 && (
                <div className="mb-2">
                  <select
                    className="form-select form-select-sm"
                    value=""
                    onChange={(e) => {
                      const tmpl = templates.find((t) => t.id === e.target.value);
                      if (tmpl) setEditedBody(tmpl.body);
                    }}
                    style={{ fontSize: 12 }}
                  >
                    <option value="">Load template…</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Regenerate hint panel — shown when Regenerate is clicked */}
              {!isReadOnly && hintOpen && (
                <div className="border rounded p-2 mb-2 bg-light">
                  <div className="small fw-semibold mb-1 d-flex justify-content-between align-items-center">
                    <span>Guidance for regeneration <span className="fw-normal text-muted">(optional)</span></span>
                    <button
                      type="button"
                      className="btn-close"
                      style={{ fontSize: 10 }}
                      onClick={() => { setHintOpen(false); setRegenerateHint(''); }}
                    />
                  </div>
                  <textarea
                    className="form-control form-control-sm mb-2"
                    rows={2}
                    value={regenerateHint}
                    onChange={(e) => setRegenerateHint(e.target.value)}
                    placeholder="e.g. 'Mention the 10% loyalty discount' or 'Keep it shorter'"
                    disabled={regenerating}
                    style={{ fontSize: 12, resize: 'vertical' }}
                  />
                  <button
                    className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                    onClick={handleRegenerate}
                    disabled={regenerating || submitting}
                  >
                    {regenerating
                      ? <><span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} /> Generating…</>
                      : <><RefreshCw size={12} /> Regenerate</>}
                  </button>
                </div>
              )}

              <textarea
                className="form-control font-monospace"
                rows={12}
                value={editedBody}
                onChange={(e) => setEditedBody(e.target.value)}
                disabled={isReadOnly || submitting || regenerating}
                style={{ fontSize: 13, lineHeight: 1.6, resize: 'vertical' }}
                placeholder={manualMode ? 'Write your reply…' : 'Agent draft will appear here…'}
              />
              <div className="text-end text-muted mt-1" style={{ fontSize: 11 }}>
                {charCount.toLocaleString()} characters
              </div>
            </div>
          </div>

          {/* Action buttons */}
          {!isReadOnly && (
            <div className="card border-0 shadow-sm">
              <div className="card-body py-3">
                <div className="d-flex flex-wrap gap-2">
                  <button
                    className="btn btn-primary d-flex align-items-center gap-1"
                    onClick={() => openConfirm('approved')}
                    disabled={submitting || !editedBody.trim()}
                  >
                    <Send size={14} /> Approve &amp; Send
                  </button>
                  <button
                    className="btn btn-secondary d-flex align-items-center gap-1"
                    onClick={() => openConfirm('edited_and_approved')}
                    disabled={submitting || !editedBody.trim()}
                  >
                    <Send size={14} /> Edit &amp; Send
                  </button>
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-1"
                    onClick={handlePushToDraft}
                    disabled={submitting || !editedBody.trim()}
                  >
                    <FileText size={14} /> Push to Gmail Draft
                  </button>
                  <button
                    className="btn btn-outline-danger d-flex align-items-center gap-1"
                    onClick={() => { setActionError(''); setShowReject(true); }}
                    disabled={submitting}
                  >
                    Reject
                  </button>
                  <button
                    className="btn btn-outline-warning d-flex align-items-center gap-1"
                    onClick={() => { setActionError(''); setShowEscalate(true); }}
                    disabled={submitting}
                  >
                    <AlertTriangle size={14} /> Escalate to Human
                  </button>
                </div>
                {submitting && (
                  <div className="text-muted small mt-2 d-flex align-items-center gap-1">
                    <span className="spinner-border spinner-border-sm" /> Processing…
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Approved — send pending, show cancel window */}
          {ticket.status === 'approved' && (
            <div className="alert alert-info py-2 d-flex align-items-center gap-2 mt-3">
              <Clock size={15} className="flex-shrink-0" />
              <span className="small flex-grow-1">
                Reply queued — will be sent after the delay window. You can still cancel.
              </span>
              <button
                className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                onClick={handleCancelSend}
                disabled={cancellingSend}
              >
                {cancellingSend
                  ? <span className="spinner-border spinner-border-sm" />
                  : 'Cancel Send'}
              </button>
            </div>
          )}

          {/* Sent state notice — with body of what was sent + wait-time indicator */}
          {ticket.status === 'sent' && (
            <div className="mt-3">
              {(() => {
                const hours     = ticket.updatedAt ? (Date.now() - new Date(ticket.updatedAt).getTime()) / (1000 * 60 * 60) : 0;
                const days      = Math.floor(hours / 24);
                const waitLabel = hours >= 72 ? `No reply in ${days} day${days !== 1 ? 's' : ''}` : hours >= 24 ? `No reply in ${days} day${days !== 1 ? 's' : ''}` : null;
                const alertCls  = hours >= 72 ? 'alert-danger' : hours >= 24 ? 'alert-warning' : 'alert-success';
                return (
                  <div className={`alert ${alertCls} py-2 d-flex align-items-center gap-2`}>
                    <Send size={15} className="flex-shrink-0" />
                    <span>
                      Reply sent via Gmail.
                      {waitLabel ? <strong className="ms-1">{waitLabel} — customer has not responded.</strong> : ' Awaiting customer reply.'}
                    </span>
                  </div>
                );
              })()}
              {(() => {
                const sentAction = ticket.actions?.find(
                  (a) => (a.actionType === 'approved' || a.actionType === 'edited_and_approved') && a.finalBodySent
                );
                return sentAction?.finalBodySent ? (
                  <div className="card border-0 shadow-sm mt-2">
                    <div className="card-header bg-light py-2">
                      <span className="fw-semibold small">Sent Reply</span>
                      <span className="text-muted ms-2 small">
                        by {sentAction.reviewerName || 'staff'} · {fmt(sentAction.createdAt)}
                      </span>
                    </div>
                    <div className="card-body py-3">
                      <pre
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'inherit',
                          fontSize: 13,
                          lineHeight: 1.6,
                          margin: 0,
                        }}
                      >
                        {sentAction.finalBodySent}
                      </pre>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {ticket.status === 'rejected' && (
            <div className="alert alert-danger py-2 mt-3">
              Ticket has been rejected and closed.
            </div>
          )}
        </div>

        {/* ── Right column (5/12): context + history ── */}
        <div className="col-12 col-lg-5">

          {/* Agent reasoning */}
          {ticket.draft?.reasoningSummary && (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-light py-2">
                <span className="fw-semibold small">Agent Reasoning</span>
              </div>
              <div className="card-body py-3">
                <p className="text-muted small mb-3" style={{ lineHeight: 1.6 }}>
                  {ticket.draft.reasoningSummary}
                </p>

                {/* Confidence */}
                <div className="mb-2">
                  <div className="text-muted mb-1" style={{ fontSize: 11 }}>Confidence Score</div>
                  <ConfidenceBar score={ticket.draft.confidenceScore} />
                </div>

                {/* Model + prompt version */}
                <div className="d-flex gap-3 mt-3" style={{ fontSize: 11, color: '#888' }}>
                  <span>Model: <span className="text-dark">{ticket.draft.modelUsed || '—'}</span></span>
                  <span>Prompt v{ticket.draft.promptVersion || '—'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Tone flags */}
          {toneFlags.length > 0 && (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-light py-2">
                <span className="fw-semibold small">Tone Flags</span>
              </div>
              <div className="card-body py-2 d-flex flex-wrap gap-1">
                {toneFlags.map((flag) => (
                  <span
                    key={flag}
                    className={`badge bg-${TONE_COLORS[flag] || 'secondary'}`}
                    style={{ fontSize: 11 }}
                  >
                    {flag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Customer context */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-light py-2 d-flex align-items-center gap-2">
              <span className="fw-semibold small">Customer Context</span>
              {c7 && <span className="text-muted small">via Commerce7</span>}
            </div>
            <div className="card-body py-3">
              {c7 ? (
                <div style={{ fontSize: 13 }}>

                  {/* Identity row */}
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                    <span className="fw-semibold">
                      {c7.firstName} {c7.lastName}
                    </span>
                    {c7.clubStatus === 'Active' && (
                      <span className="badge bg-success" style={{ fontSize: 10 }}>Club Active</span>
                    )}
                    {c7.clubStatus === 'Cancelled' && (
                      <span className="badge bg-secondary" style={{ fontSize: 10 }}>Club Cancelled</span>
                    )}
                    {c7.clubTier && (
                      <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>{c7.clubTier}</span>
                    )}
                  </div>

                  {/* On-hold warning — high-priority signal for CS staff */}
                  {Array.isArray(c7.onHolds) && c7.onHolds.length > 0 && (
                    <div className="alert alert-warning py-2 px-2 mb-2 d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                      <ShieldAlert size={14} className="flex-shrink-0" />
                      <span>
                        <strong>Club on hold</strong>
                        {c7.onHolds[0]?.reason ? `: ${c7.onHolds[0].reason}` : ''}
                      </span>
                    </div>
                  )}

                  <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                    <tbody>
                      {c7.email && (
                        <tr>
                          <td className="text-muted ps-0" style={{ width: '40%' }}>Email</td>
                          <td className="pe-0">{c7.email}</td>
                        </tr>
                      )}
                      {c7.phone && (
                        <tr>
                          <td className="text-muted ps-0">Phone</td>
                          <td className="pe-0">{c7.phone}</td>
                        </tr>
                      )}
                      {(c7.city || c7.stateCode) && (
                        <tr>
                          <td className="text-muted ps-0">Location</td>
                          <td className="pe-0">{[c7.city, c7.stateCode].filter(Boolean).join(', ')}</td>
                        </tr>
                      )}
                      {c7.lifetimeValue != null && (
                        <tr>
                          <td className="text-muted ps-0">Lifetime Value</td>
                          <td className="pe-0 fw-semibold text-success">
                            ${(c7.lifetimeValue / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      )}
                      {c7.totalOrders != null && (
                        <tr>
                          <td className="text-muted ps-0">Total Orders</td>
                          <td className="pe-0">{c7.totalOrders}</td>
                        </tr>
                      )}
                      {c7.outstandingBalance != null && c7.outstandingBalance > 0 && (
                        <tr>
                          <td className="text-muted ps-0">Outstanding</td>
                          <td className="pe-0 text-danger fw-semibold">
                            ${(c7.outstandingBalance / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      )}
                      {c7.preferredVarietal && (
                        <tr>
                          <td className="text-muted ps-0">Preferred</td>
                          <td className="pe-0">{c7.preferredVarietal}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Recent orders — with item titles when available */}
                  {c7.recentOrders?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-muted fw-semibold mb-2" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Recent Orders
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {c7.recentOrders.slice(0, 3).map((ord, i) => (
                          <div key={i} className="border rounded p-2" style={{ fontSize: 12 }}>
                            <div className="d-flex justify-content-between align-items-baseline mb-1">
                              <span className="text-muted">{fmt(ord.orderDate || ord.createdAt)}</span>
                              <span className="fw-semibold">
                                ${((ord.total || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            {ord.items?.length > 0 && (
                              <ul className="mb-0 ps-3" style={{ fontSize: 11, color: '#555' }}>
                                {ord.items.slice(0, 3).map((item, j) => (
                                  <li key={j}>
                                    {item.qty ? `${item.qty}× ` : ''}{item.title || item.sku || 'Item'}
                                  </li>
                                ))}
                                {ord.items.length > 3 && (
                                  <li className="text-muted">+{ord.items.length - 3} more</li>
                                )}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted small mb-0">No C7 context available for this ticket.</p>
              )}
            </div>
          </div>

          {/* Email thread accordion — lazy-fetches Gmail thread on open */}
          <div className="card border-0 shadow-sm mb-3">
            <div
              className="card-header bg-light py-2 d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={handleThreadOpen}
            >
              <span className="fw-semibold small d-flex align-items-center gap-2">
                Email Thread
                {threadFetched && threadMessages.length > 0 && (
                  <span className="badge bg-secondary" style={{ fontSize: 10 }}>
                    {threadMessages.length}
                  </span>
                )}
              </span>
              {historyOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
            {historyOpen && (
              <div className="card-body py-2">
                {threadLoading ? (
                  <div className="text-center py-3">
                    <span className="spinner-border spinner-border-sm text-secondary" />
                  </div>
                ) : threadError ? (
                  <p className="text-danger small mb-0">{threadError}</p>
                ) : threadMessages.length === 0 ? (
                  <p className="text-muted small mb-0">No prior messages in this thread.</p>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {threadMessages.map((msg, i) => (
                      <div key={msg.messageId || i} className="border-start border-2 ps-3" style={{ borderColor: '#dee2e6' }}>
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <span className="small fw-semibold text-truncate" style={{ maxWidth: '70%' }}>
                            {msg.from || '(Unknown sender)'}
                          </span>
                          <span className="text-muted flex-shrink-0 ms-2" style={{ fontSize: 11 }}>
                            {msg.date ? new Date(msg.date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                        <pre
                          className="text-muted small mb-0"
                          style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'inherit',
                            lineHeight: 1.5,
                            maxHeight: 150,
                            overflowY: 'auto',
                          }}
                        >
                          {(msg.bodyText || '').slice(0, 500)}
                          {(msg.bodyText || '').length > 500 ? '\n…' : ''}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Assigned rep */}
          {ticket.assignedRepName && (
            <div className="text-muted small d-flex align-items-center gap-1 mb-3">
              <UserCheck size={13} /> Assigned to {ticket.assignedRepName}
            </div>
          )}

          {/* Prior tickets from same customer */}
          {relatedTickets.length > 0 && (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-light py-2">
                <span className="fw-semibold small">
                  Prior Tickets
                  <span className="badge bg-secondary ms-2" style={{ fontSize: 10 }}>
                    {relatedTickets.length}
                  </span>
                </span>
              </div>
              <div className="card-body py-0">
                {relatedTickets.map((rt, i) => (
                  <div
                    key={rt.ticketID}
                    className={`d-flex justify-content-between align-items-start py-2 ${i < relatedTickets.length - 1 ? 'border-bottom' : ''}`}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p
                        className="mb-0 small fw-semibold text-truncate"
                        style={{ maxWidth: '100%' }}
                        title={rt.subject}
                      >
                        {rt.subject || '(no subject)'}
                      </p>
                      <span className="badge bg-light text-dark border mt-1" style={{ fontSize: 10 }}>
                        {INTENT_LABELS[rt.intentType] || rt.intentType || 'Unknown'}
                      </span>
                    </div>
                    <div className="text-end ms-2 flex-shrink-0">
                      <span
                        className={`badge bg-${STATUS_COLORS[rt.status] || 'secondary'}`}
                        style={{ fontSize: 10 }}
                      >
                        {rt.status}
                      </span>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        {fmt(rt.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Internal notes */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-light py-2 d-flex align-items-center gap-2">
              <MessageSquare size={14} />
              <span className="fw-semibold small">
                Internal Notes
                {notes.length > 0 && (
                  <span className="badge bg-secondary ms-2" style={{ fontSize: 10 }}>
                    {notes.length}
                  </span>
                )}
              </span>
              <span className="text-muted ms-auto" style={{ fontSize: 11 }}>
                Never sent to customer
              </span>
            </div>
            <div className="card-body py-3">
              {/* Existing notes */}
              {notes.length > 0 ? (
                <div className="d-flex flex-column gap-3 mb-3">
                  {notes.map((note) => {
                    const isOwn   = note.authorID === user?.id;
                    const isAdmin = user?.role === 'admin';
                    return (
                      <div key={note.noteID} className="d-flex gap-2">
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-baseline mb-1">
                            <span className="fw-semibold" style={{ fontSize: 12 }}>
                              {note.authorName || 'Unknown'}
                            </span>
                            <span className="text-muted" style={{ fontSize: 11 }}>
                              {fmt(note.createdAt)}
                            </span>
                          </div>
                          <p
                            className="mb-0 text-dark"
                            style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                          >
                            {note.noteText}
                          </p>
                        </div>
                        {(isOwn || isAdmin) && (
                          <button
                            className="btn btn-sm btn-link text-danger p-0 ms-1 flex-shrink-0 align-self-start"
                            style={{ fontSize: 12 }}
                            onClick={() => handleDeleteNote(note.noteID)}
                            disabled={!!deletingNote[note.noteID]}
                            title="Delete note"
                          >
                            {deletingNote[note.noteID]
                              ? <span className="spinner-border spinner-border-sm" />
                              : <Trash2 size={13} />}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted small mb-3">No internal notes yet.</p>
              )}

              {/* Add note */}
              <div>
                <textarea
                  className="form-control form-control-sm mb-2"
                  rows={3}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note for your team…"
                  disabled={savingNote}
                  style={{ fontSize: 13, resize: 'vertical' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddNote();
                  }}
                />
                <button
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={handleAddNote}
                  disabled={savingNote || !noteText.trim()}
                >
                  {savingNote
                    ? <span className="spinner-border spinner-border-sm" />
                    : <><MessageSquare size={12} /> Add Note</>}
                </button>
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                  ⌘↵ or Ctrl+↵ to save
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Confirm (Approve/Edit & Send) Modal ─────────────────────────────── */}
      {showConfirm && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowConfirm(false); }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Confirm — Approve &amp; Send</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirm(false)}
                  disabled={submitting}
                />
              </div>
              <div className="modal-body">

                {/* Countdown */}
                <div className={`alert py-2 ${countdown > 0 ? 'alert-warning' : 'alert-success'} d-flex align-items-center gap-2`}>
                  <Clock size={15} />
                  {countdown > 0
                    ? <>Send delay: <strong>{countdown}s</strong> remaining — email will be queued and sent after delay expires.</>
                    : <>Delay window has passed — email will be sent immediately upon confirmation.</>}
                </div>

                {/* Final body preview */}
                <div className="mb-2">
                  <div className="fw-semibold small mb-1">Reply preview</div>
                  <div
                    className="border rounded p-3 bg-light"
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: 13,
                      lineHeight: 1.6,
                      maxHeight: 300,
                      overflowY: 'auto',
                      fontFamily: 'inherit',
                    }}
                  >
                    {editedBody || '(empty)'}
                  </div>
                </div>

                <div className="text-muted small">
                  To: <strong>{ticket.customerEmail}</strong>
                </div>

                {actionError && (
                  <div className="alert alert-danger py-2 mt-3">{actionError}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowConfirm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary d-flex align-items-center gap-1"
                  onClick={handleApprove}
                  disabled={submitting}
                >
                  {submitting
                    ? <span className="spinner-border spinner-border-sm" />
                    : <Send size={14} />}
                  {submitting ? 'Sending…' : 'Confirm &amp; Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ─────────────────────────────────────────────────────── */}
      {showReject && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowReject(false); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Reject Ticket</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowReject(false)}
                  disabled={submitting}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Categorise the rejection to improve AI routing over time.
                </p>

                {/* Structured rejection category */}
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Rejection Category</label>
                  <select
                    className="form-select form-select-sm"
                    value={rejectionCategory}
                    onChange={(e) => setRejectionCategory(e.target.value)}
                    disabled={submitting}
                  >
                    {REJECTION_CATEGORIES.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Free-text notes — required only when no specific category or category is 'other' */}
                {(() => {
                  const noteRequired = !rejectionCategory || rejectionCategory === 'other';
                  return (
                    <div className="mb-0">
                      <label className="form-label small fw-semibold">
                        Notes{' '}
                        <span className="text-muted fw-normal">
                          {noteRequired ? '(required)' : '(optional)'}
                        </span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder={noteRequired ? 'Brief notes for the team…' : 'Any additional context… (optional)'}
                        disabled={submitting}
                      />
                    </div>
                  );
                })()}

                {actionError && (
                  <div className="alert alert-danger py-2 mt-3">{actionError}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowReject(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleReject}
                  disabled={submitting || ((!rejectionCategory || rejectionCategory === 'other') && !rejectReason.trim())}
                >
                  {submitting
                    ? <span className="spinner-border spinner-border-sm" />
                    : 'Reject Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Close Confirmation Modal ─────────────────────────────────────────── */}
      {showCloseModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowCloseModal(false); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title d-flex align-items-center gap-2">
                  <Archive size={16} /> Close Ticket
                </h6>
                <button type="button" className="btn-close" onClick={() => setShowCloseModal(false)} />
              </div>
              <div className="modal-body">
                <p className="mb-2">Archive this ticket?</p>
                <p className="text-muted small mb-0">
                  It will be hidden from the queue and moved to the <strong>Archived</strong> tab.
                  You can reopen it any time.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowCloseModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-dark d-flex align-items-center gap-1"
                  onClick={handleClose}
                >
                  <Archive size={14} /> Close Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Escalate Modal ───────────────────────────────────────────────────── */}
      {showEscalate && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowEscalate(false); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Escalate to Human</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEscalate(false)}
                  disabled={submitting}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Describe why this ticket requires human handling. The escalation level will be updated.
                </p>
                <textarea
                  className="form-control"
                  rows={4}
                  value={escalateReason}
                  onChange={(e) => setEscalateReason(e.target.value)}
                  placeholder="Reason for escalation…"
                  disabled={submitting}
                />
                {actionError && (
                  <div className="alert alert-danger py-2 mt-3">{actionError}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowEscalate(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-warning d-flex align-items-center gap-1"
                  onClick={handleEscalate}
                  disabled={submitting || !escalateReason.trim()}
                >
                  {submitting
                    ? <span className="spinner-border spinner-border-sm" />
                    : <AlertTriangle size={14} />}
                  Escalate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
